import {
  property,
  state,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';
import { DeesThumbnailBase } from '../dees-thumbnail-shared/DeesThumbnailBase.js';
import { thumbnailBaseStyles } from '../dees-thumbnail-shared/styles.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-thumbnail-image': DeesThumbnailImage;
  }
}

@customElement('dees-thumbnail-image')
export class DeesThumbnailImage extends DeesThumbnailBase {
  public static demo = demo;
  public static demoGroups = ['Media'];
  public static styles = [
    ...thumbnailBaseStyles,
    css`
      :host { --thumb-checkerboard: repeating-conic-gradient(var(--dees-color-bg-secondary) 0% 25%, var(--dees-color-bg-tertiary) 0% 50%) 50% / 16px 16px; }
      .image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: var(--thumb-checkerboard);
      }

      .image-wrapper img {
        width: 100%;
        height: 100%;
        object-fit: var(--thumbnail-fit, cover);
        display: block;
        transition: opacity 0.3s ease;
      }

      .image-wrapper img.loaded {
        opacity: 1;
      }

      .image-wrapper img.loading {
        opacity: 0;
      }

    `,
  ] as any;

  @property({ type: String })
  accessor fit: 'cover' | 'contain' = 'cover';

  @property({ type: String })
  accessor src: string = '';

  @property({ type: String })
  accessor alt: string = '';

  @state()
  accessor imageLoaded: boolean = false;

  @state()
  accessor imageWidth: number = 0;

  @state()
  accessor imageHeight: number = 0;

  private hasStartedLoading: boolean = false;

  protected renderTileContent(): TemplateResult {
    return html`
      <div class="image-wrapper" style=${`--thumbnail-fit:${this.fit}`}>
        ${this.hasStartedLoading ? html`
          <img
            class="${this.imageLoaded ? 'loaded' : 'loading'}"
            src="${this.src}"
            alt="${this.alt}"
            @load=${this.handleImageLoad}
            @error=${this.handleImageError}
          />
        ` : ''}
      </div>


    `;
  }

  protected renderBottomBar(): TemplateResult | string {
    if (!this.label && !(this.imageWidth > 0)) return '';
    return html`
      <div class="tile-info-bar">
        ${this.label ? html`<span class="info-label" title="${this.label}">${this.label}</span>` : ''}
        <span class="info-spacer"></span>
        ${this.imageWidth > 0 && this.imageHeight > 0
          ? html`<span class="info-detail">${this.imageWidth} × ${this.imageHeight}</span>`
          : ''}
      </div>
    `;
  }

  protected getTileClickDetail(): Record<string, unknown> {
    return {
      src: this.src,
      alt: this.alt,
      width: this.imageWidth,
      height: this.imageHeight,
    };
  }

  protected onBecameVisible(): void {
    if (!this.hasStartedLoading && this.src) {
      this.hasStartedLoading = true;
      this.loading = true;
      this.requestUpdate();
    }
  }

  private handleImageLoad(e: Event): void {
    const img = e.target as HTMLImageElement;
    this.imageWidth = img.naturalWidth;
    this.imageHeight = img.naturalHeight;
    this.imageLoaded = true;
    this.loading = false;
  }

  private handleImageError(): void {
    this.error = true;
    this.loading = false;
  }

  protected willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('src')) {
      this.error = false;
      this.imageLoaded = false;
      this.imageWidth = 0;
      this.imageHeight = 0;
      this.hasStartedLoading = !!this.src && this.isVisible;
      this.loading = this.hasStartedLoading;
    }
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);
  }
}
